import {
  DistanceUnitLabelType,
  DistanceUnitType,
  JobPriorityType,
  JobType,
  TimeUnitLabelType,
  TimeUnitType,
  VolumnUnitLabelType,
  VolumnUnitType,
  TorqueUnitType,
  TorqueUnitLabelType,
  DimensionUnitType,
  DimensionUnitLabelType,
  MassUnitType,
  MassUnitLabelType,
  JobPriorityLabelType,
  AssetType,
  AssetLabelType,
  ReadingType,
  ReadingLabelType,
  EquipmentType,
  EquipmentTypeLabel,
  VehicleStatusType,
  VehicleStatusLabelType,
  VehicleOwnershipType,
  OwnershipType,
  AssetStatus,
  AssetStatusLabels,
  OwnershipLabelType,
  ScheduleTimeBasedPattern,
  ScheduleTimeBasedPatternLabelType,
  DaysOfWeek,
  DaysOfWeekLabelType,
  JobScheduleType,
  JobScheduleLabelType,
  JobExpandablePropertyType,
  JobStatusNewType,
  JobStatusFilterLabels
} from '@/enum'
import {
  generateIterableListFromEnum,
  generateIterableListFromEnumWithTitleAsValue
} from '../helpers'
import { VehicleFuelLabelType, VehicleFuelType } from '@/enum/vehicles/VehicleFuelType'
import { JobLabelType } from '@/enum/JobType'
import { RolesLabelType, RoleType } from '@/enum/RoleType'
import { VehicleOwnershipLabelType } from '@/enum/vehicles/VehicleOwnershipType'
import { MachineStatusLabelType, MachineStatusType } from '@/enum/machine'
import { MachineFuelLabelType, MachineFuelType } from '@/enum/machine/MachineFuelType'

const {
  VITE_WEB_CLIENT_TYPE,
  VITE_MOBILE_CLIENT_TYPE,
  VITE_API_URL,
  VITE_DEFAULT_PAGING_LIMIT,
  VITE_ASSETS_PREFIX,
  VITE_ENV,
  VITE_APP_VERSION,
  VITE_SENTRY_DSN,
  VITE_SENTRY_AUTH_TOKEN
} = import.meta.env

//app
export const DASHBOARD_DOMAINS = {
  DEVELOPMENT: { URL: 'https://web-dev-jsalton.thumbmunkeys.com', ENV_NAME: 'Development' },
  STAGING: { URL: '', ENV_NAME: 'Staging' },
  DEMO: { URL: 'https://web-demo.jsalton.co.uk/', ENV_NAME: 'Demo' },
  PRODUCTION: { URL: 'https://web.jsalton.co.uk/', ENV_NAME: 'Production' }
}
export const BASE_URL = '/'
export const BASE_API_URL = VITE_API_URL
export const APP_NAME = 'JS Alton'
export const APP_ASSETS_PREFIX = VITE_ASSETS_PREFIX
export const APP_ENV = VITE_ENV
export const APP_VERSION = VITE_APP_VERSION

//general
export const DEFAULT_LIST_EXPORT_FORMAT = 'csv'
export const DASHBOARD_CLIENT_TYPE = VITE_WEB_CLIENT_TYPE
export const MOBILE_CLIENT_TYPE = VITE_MOBILE_CLIENT_TYPE
export const NOTIFICATIONS_NAME = 'Notifications'
export const DEFAULT_PAGE_OFFSET = 0
export const DEFAULT_SELECTED_PAGE_NUMBER = 1
export const DEFAULT_PAGE_LIMIT = +VITE_DEFAULT_PAGING_LIMIT
export const CANCELED_REQUEST_CODE = 'ERR_CANCELED'
export const NETWORK_ERROR_CODE = 'ERR_NETWORK'
export const CANCELLED_REQUEST_STATUS = 0
export const NUMBER_OF_MILLISECONDS_IN_DAY = 86400000
export const DEFAULT_TIMEOUT_BUFFER = 500
export const DEFAULT_DISTANCE_UNIT = 'miles'
export const DEFAULT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365
export const HOURS_IN_DAY = 24
export const MINUTES_INTERVAL = 15
export const MINUTES_IN_HOUR = (() => {
  const minutes = []
  let i = 0
  while (i < 60) {
    minutes.push(i)
    i += MINUTES_INTERVAL
  }
  return minutes
})()
export const DEFAULT_MAXIMUM_DAYS_IN_DATE_RANGE = 90
export const MAX_FILE_SIZE = 25
export const MAX_INPUT_NUMBER = 99999999
export const DISABLE_SUBMIT_BUTTON_TIMEOUT_IN_SECONDS = 30
export const DEFAULT_JOB_SCHEDULE_NUMBER_OF_OCCURRENCES = 1
export const DEFAULT_JOB_PRIORITY = 0
export const DEFAULT_TEMPLATE_INPUT_IS_OPTIONAL = false
export const WORKER_SIGNAL_R = 'signalRWorker'
export const GLOBAL_ERROR_TYPES = {
  COMPONENT: 'Component Error',
  NETWORK: 'Network Error',
  FILE: 'File Error',
  UNKNOWN: 'Unknown Error'
}
export const DEFAULT_JOB_START_TIME = '00:00:00'
export const DEFAULT_JOB_END_TIME = '23:59:00'
export const MINIMUM_FUEL_ENTRY_QUANTITY = 0.5
export const MILL_ORDER_TEMPLATE_ID = '96a09337-8c4e-448d-80eb-1ee843b602ce'

//sentry
export const SENTRY_DSN_KEY = VITE_SENTRY_DSN
export const SENTRY_AUTH_KEY = VITE_SENTRY_AUTH_TOKEN
export const SENTRY_TRACES_SAMPLE_RATE = 1.0
export const SENTRY_REPLAYS_SESSION_SAMPLE_RATE = VITE_ENV == 'Production' ? 0.5 : 1.0
export const SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE = 1.0

//mill orders
export const MILL_ORDER_DISABLED_COLUMN_INDEXES = [2, 3, 4, 5]

export const MILL_ORDER_ENABLED_SETTINGS_COLUMN_INDEXES = [0, 1]

//iterable
export const JOB_TYPES = (() => generateIterableListFromEnum(JobType, JobLabelType))()
export const JOB_SCHEDULE_TYPES = (() =>
  generateIterableListFromEnum(JobScheduleType, JobScheduleLabelType))()
export const JOB_PRIORITY_TYPES = (() =>
  generateIterableListFromEnum(JobPriorityType, JobPriorityLabelType))()
export const JOB_STATUS_TYPES = (() =>
  generateIterableListFromEnum(JobStatusNewType, JobStatusFilterLabels))()
export const DISTANCE_UNIT_TYPES = (() =>
  generateIterableListFromEnumWithTitleAsValue(DistanceUnitType, DistanceUnitLabelType))()
export const VOLUME_UNIT_TYPES = (() =>
  generateIterableListFromEnumWithTitleAsValue(VolumnUnitType, VolumnUnitLabelType))()
export const VEHICLE_FUEL_TYPES = (() =>
  generateIterableListFromEnumWithTitleAsValue(VehicleFuelType, VehicleFuelLabelType))()
export const MACHINE_FUEL_TYPES = (() =>
  generateIterableListFromEnumWithTitleAsValue(MachineFuelType, MachineFuelLabelType))()
export const TIME_UNIT_TYPES = (() =>
  generateIterableListFromEnumWithTitleAsValue(TimeUnitType, TimeUnitLabelType))()
export const ROLE_TYPES = (() =>
  generateIterableListFromEnumWithTitleAsValue(RoleType, RolesLabelType))()
export const TORQUE_UNIT_TYPES = (() =>
  generateIterableListFromEnumWithTitleAsValue(TorqueUnitType, TorqueUnitLabelType))()
export const DIMENSION_UNIT_TYPES = (() =>
  generateIterableListFromEnumWithTitleAsValue(DimensionUnitType, DimensionUnitLabelType))()
export const MASS_UNIT_TYPES = (() =>
  generateIterableListFromEnumWithTitleAsValue(MassUnitType, MassUnitLabelType))()
export const ASSET_TYPES = (() =>
  generateIterableListFromEnumWithTitleAsValue(AssetType, AssetLabelType))()
export const ASSET_TYPES_VALUES = (() => generateIterableListFromEnum(AssetType, AssetLabelType))()
export const READING_TYPES = (() => generateIterableListFromEnum(ReadingType, ReadingLabelType))()
export const VEHICLE_STATUS_TYPES = (() =>
  generateIterableListFromEnum(VehicleStatusType, VehicleStatusLabelType))()
export const MACHINES_STATUS_TYPES = (() =>
  generateIterableListFromEnum(MachineStatusType, MachineStatusLabelType))()
export const OWNERSHIP_TYPES = (() =>
  generateIterableListFromEnum(OwnershipType, OwnershipLabelType))()
export const MACHINE_STATUS_TYPES = (() =>
  generateIterableListFromEnum(AssetStatus, AssetStatusLabels))()
export const VEHICLE_OWNERSHIP_TYPES = (() =>
  generateIterableListFromEnum(VehicleOwnershipType, VehicleOwnershipLabelType))()
export const EQUIPMENT_TYPES = (() =>
  generateIterableListFromEnumWithTitleAsValue(EquipmentType, EquipmentTypeLabel))()
export const YEARS = (() => {
  const currentYear = new Date().getFullYear()
  const years: string[] = []
  for (let i = currentYear; i >= 1900; i--) {
    years.push(String(i))
  }
  return generateIterableListFromEnumWithTitleAsValue(years, years)
})()
export const TIME_BASED_TRIGGER_TYPES = (() =>
  generateIterableListFromEnum(ScheduleTimeBasedPattern, ScheduleTimeBasedPatternLabelType))()
export const DAYS_OF_WEEK_TRIGGER_TYPES = (() =>
  generateIterableListFromEnum(DaysOfWeek, DaysOfWeekLabelType))()
export const JOBS_LIST_EXPAND_PARAMS = (() => {
  const expandableResources = [
    JobExpandablePropertyType.ASSIGNED_EMPLOYEES,
    JobExpandablePropertyType.ASSIGNED_EMPLOYEES_EMPLOYEE,
    JobExpandablePropertyType.TASK_TEMPLATES,
    JobExpandablePropertyType.ASSET,
    JobExpandablePropertyType.ASSET_EQUIPMENT,
    JobExpandablePropertyType.ASSET_MACHINE,
    JobExpandablePropertyType.ASSET_VEHICLE,
    JobExpandablePropertyType.ISSUE_REPORTER,
    JobExpandablePropertyType.NEXT_TRIGGER_INFO
  ]
  return expandableResources.join(',')
})()
export const JOB_DETAILS_EXPAND_PARAMS = (() => {
  const expandableResources = [
    JobExpandablePropertyType.ASSIGNED_EMPLOYEES,
    JobExpandablePropertyType.ASSIGNED_EMPLOYEES_EMPLOYEE,
    JobExpandablePropertyType.TASK_TEMPLATES,
    // JobExpandablePropertyType.TASK_TEMPLATES_SECTIONS,
    JobExpandablePropertyType.WORK_ORDER_VENDOR,
    JobExpandablePropertyType.ASSET,
    JobExpandablePropertyType.ASSET_INFO,
    JobExpandablePropertyType.ASSET_EQUIPMENT,
    JobExpandablePropertyType.ASSET_MACHINE,
    JobExpandablePropertyType.ASSET_VEHICLE,
    JobExpandablePropertyType.ISSUE_REPORTER,
    JobExpandablePropertyType.DOCUMENTS,
    JobExpandablePropertyType.MAINTENANCE_TYPES,
    JobExpandablePropertyType.NEXT_TRIGGER_INFO
  ]
  return expandableResources.join(',')
})()
export const UNSCHEDULED_JOBS_LIST_EXPAND_PARAMS = (() => {
  const expandableResources = [
    JobExpandablePropertyType.ASSIGNED_EMPLOYEES,
    JobExpandablePropertyType.ASSIGNED_EMPLOYEES_EMPLOYEE,
    JobExpandablePropertyType.JOB
    // JobExpandablePropertyType.SCHEDULE,
    // JobExpandablePropertyType.ASSET,
    // JobExpandablePropertyType.ASSET_VEHICLE,
    // JobExpandablePropertyType.ASSET_MACHINE,
    // JobExpandablePropertyType.ASSET_EQUIPMENT
  ]
  return expandableResources.join(',')
})()
